import { CircleInfoIcon, TrashIcon } from "@xola/icons";
import { Label, Tooltip } from "@xola/ui-kit";
import React, { useRef } from "react";
import { SecondaryButton } from "../SecondaryButton";

export const MAX_ATTACHMENT_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
export const ALLOWED_ATTACHMENT_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
]);

interface AttachmentFieldProps {
    attachment?: File;
    attachmentError?: string;
    onAttachmentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onAttachmentRemove: () => void;
}

export const AttachmentField = ({
    attachment,
    attachmentError,
    onAttachmentChange,
    onAttachmentRemove,
}: AttachmentFieldProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const sizeLabel = attachment ? `${(attachment.size / (1024 * 1024)).toFixed(1)} MB` : "";

    const handleRemove = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        onAttachmentRemove();
    };

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
                <Label className="mb-0">Attachment</Label>
                <Tooltip content="Maximum 1 file per message">
                    <CircleInfoIcon className="cursor-default align-middle text-gray" size="small" />
                </Tooltip>
            </div>

            {attachment && (
                <div className="flex items-center justify-between rounded border border-gray-light px-4 py-2">
                    <div className="flex min-w-0 items-center gap-1">
                        <span className="truncate font-bold">{attachment.name}</span>
                        <span className="shrink-0 rounded bg-gray-lighter px-1 py-0.5 text-xs text-gray-darker">
                            {sizeLabel}
                        </span>
                    </div>
                    <SecondaryButton size="small" onClick={handleRemove}>
                        <TrashIcon size="small" />
                    </SecondaryButton>
                </div>
            )}

            <div>
                <Tooltip content="Maximum 1 file per message" placement="right" disabled={!attachment}>
                    <SecondaryButton
                        size="small"
                        disabled={!!attachment}
                        onClick={() => !attachment && fileInputRef.current?.click()}
                    >
                        + Attach File
                    </SecondaryButton>
                </Tooltip>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={onAttachmentChange}
            />
            {attachmentError && <p className="text-sm text-danger">{attachmentError}</p>}
        </div>
    );
};
